<template>
  <div class="payment-management-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>付款管理</span>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-select
        v-model="searchParams.paymentMethod"
        placeholder="付款方式"
        clearable
        class="search-input"
      >
        <el-option label="现金" value="CASH"></el-option>
        <el-option label="转账" value="BANK_TRANSFER"></el-option>
        <el-option label="移动支付" value="MOBILE_PAYMENT"></el-option>
        <el-option label="汇款" value="REMITTANCE"></el-option>
      </el-select>
      
      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="查询时间起"
        aria-label="查询时间起"
        value-format="yyyy-MM-dd"
        clearable
        class="search-input date-picker"
      ></el-date-picker>
      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="查询时间止"
        aria-label="查询时间止"
        value-format="yyyy-MM-dd"
        clearable
        class="search-input date-picker"
      ></el-date-picker>
      
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      
      <!-- 新增付款按钮 -->
      <el-button v-permission="'settlement:payment-management:create'" type="success" @click="showAddPaymentDialog" icon="el-icon-plus" style="margin-left: 10px;">新增付款</el-button>
    </div>
    
    <!-- 付款列表 -->
    <el-table
      :data="paymentList"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column
        label="付款时间"
        width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.paymentDate) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="付款金额"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatAmount(scope.row.amount) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="付款方式"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          <el-tag :type="getPaymentMethodTagType(scope.row.paymentMethod)" size="small">
            {{ getPaymentMethodName(scope.row.paymentMethod) }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        label="关联结算单"
        min-width="200"
        align="center"
      >
        <template slot-scope="scope">
          <div v-if="scope.row.settlements && scope.row.settlements.length > 0">
            <el-tag
              v-for="(settlement, index) in scope.row.settlements"
              :key="index"
              type="info"
              size="small"
              style="margin-right: 5px;"
            >
              {{ settlement.settlementNumber || settlement.settlement_no }}
            </el-tag>
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column
        label="付款凭证"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.voucherUrl"
            :src="getAttachmentUrl(scope.row.voucherUrl)"
            :preview-src-list="[getAttachmentUrl(scope.row.voucherUrl)]"
            style="width: 60px; height: 60px; cursor: pointer;"
            fit="cover"
          ></el-image>
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column
        label="备注"
        width="150"
        align="center"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          {{ scope.row.remark || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="创建时间"
        width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="180"
        align="center"
        fixed="right"
      >
        <template slot-scope="scope">
          <el-button
            v-permission="'settlement:payment-management:edit'"
            type="primary"
            size="mini"
            @click="editPayment(scope.row)"
          >编辑</el-button>
          <el-button
            v-permission="'settlement:payment-management:delete'"
            type="danger"
            size="mini"
            @click="deletePayment(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.current"
        :page-sizes="[10, 20, 30, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      ></el-pagination>
    </div>
    
    <!-- 新增/编辑付款对话框 -->
    <el-dialog
      v-dialog-drag
      :title="paymentDialogTitle"
      :visible.sync="paymentDialogVisible"
      width="600px"
      top="8px"
      :close-on-click-modal="false"
      @close="resetPaymentForm"
    >
      <el-form
        :model="paymentForm"
        :rules="paymentFormRules"
        ref="paymentForm"
        label-width="120px"
      >
        <el-form-item label="付款时间" prop="paymentDate">
          <el-date-picker
            v-model="paymentForm.paymentDate"
            type="datetime"
            placeholder="选择付款时间"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        
        <el-form-item label="付款金额" prop="amount">
          <el-input-number
            v-model="paymentForm.amount"
            :precision="2"
            :min="0"
            style="width: 100%"
            placeholder="请输入付款金额"
          ></el-input-number>
        </el-form-item>
        
        <el-form-item label="付款方式" prop="paymentMethod">
          <el-select
            v-model="paymentForm.paymentMethod"
            placeholder="请选择付款方式"
            style="width: 100%"
          >
            <el-option label="现金" value="CASH"></el-option>
            <el-option label="转账" value="BANK_TRANSFER"></el-option>
            <el-option label="移动支付" value="MOBILE_PAYMENT"></el-option>
            <el-option label="汇款" value="REMITTANCE"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="付款凭证" prop="voucherUrl">
          <el-upload
            ref="voucherUpload"
            class="payment-uploader"
            :http-request="handleVoucherUpload"
            :before-upload="beforeFileUpload"
            :show-file-list="false"
            accept=".jpg,.jpeg,.png,.pdf"
          >
            <img v-if="voucherPreview" :src="voucherPreview" class="payment-image" />
            <div v-else class="upload-placeholder">
              <i class="el-icon-plus payment-uploader-icon"></i>
            </div>
            <div slot="tip" class="el-upload__tip">支持jpg/png/pdf文件，且不超过10MB</div>
          </el-upload>
          <div v-if="paymentForm.voucherUrl" class="file-path">
            <el-tag size="small" closable @close="clearVoucher">{{ paymentForm.voucherUrl }}</el-tag>
          </div>
        </el-form-item>
        
        <el-form-item label="备注" prop="remark">
          <el-input
            v-model="paymentForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注信息（可选）"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="关联结算单" prop="settlementIds">
          <el-select
            v-model="paymentForm.settlementIds"
            multiple
            filterable
            placeholder="请选择关联的结算单（可多选）"
            style="width: 100%"
          >
            <el-option
              v-for="settlement in availableSettlements"
              :key="settlement.id"
              :label="`${settlement.settlementNumber || settlement.settlement_no} - ${settlement.settlementTitle || settlement.settlement_title}`"
              :value="settlement.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="paymentDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitPaymentForm" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { save_payment, query_payments_pagination, query_payment_by_id, delete_payment } from '@/api/modules/payment'
import { upload_attachment } from '@/api/modules/attachment'
import { query_settlements_pagination } from '@/api/modules/settlement'
import { getApiUrl } from '@/utils/env'

export default {
  name: 'PaymentManagement',
  data() {
    return {
      loading: false,
      searchParams: {
        paymentMethod: ''
      },
      startDate: '',
      endDate: '',
      paymentList: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      
      // 付款对话框
      paymentDialogVisible: false,
      paymentDialogTitle: '新增付款',
      submitLoading: false,
      
      // 付款表单
      paymentForm: {
        id: null,
        paymentDate: '',
        amount: null,
        paymentMethod: '',
        voucherUrl: '',
        remark: '',
        settlementIds: []
      },
      
      // 文件预览
      voucherPreview: '',
      
      // 表单验证规则
      paymentFormRules: {
        paymentDate: [
          { required: true, message: '请选择付款时间', trigger: 'change' }
        ],
        amount: [
          { required: true, message: '请输入付款金额', trigger: 'blur' },
          { type: 'number', min: 0, message: '付款金额必须大于0', trigger: 'blur' }
        ],
        paymentMethod: [
          { required: true, message: '请选择付款方式', trigger: 'change' }
        ],
        settlementIds: [
          { required: true, type: 'array', min: 1, message: '请至少选择一个结算单', trigger: 'change' }
        ]
      },
      
      // 可选的结算单列表（只包含已确认状态的结算单）
      availableSettlements: []
    }
  },
  created() {
    // 初始化时加载付款列表和结算单列表
    this.loadPaymentList()
    this.loadAvailableSettlements()
  },
  methods: {
    // 加载可用的结算单列表（只包含已确认状态的结算单）
    async loadAvailableSettlements() {
      try {
        // 查询状态为EXECUTING（已确认）的结算单
        const params = {
          status: 'EXECUTING',
          current: 1,
          pageSize: 1000 // 获取足够多的结算单，如果超过1000条可能需要分页
        }
        
        const response = await query_settlements_pagination(params)
        
        if (response.data && response.data.success) {
          const data = response.data.data || {}
          
          // 处理不同的响应格式
          if (data.content) {
            this.availableSettlements = data.content || []
          } else if (data.list) {
            this.availableSettlements = data.list || []
          } else if (Array.isArray(response.data.data)) {
            this.availableSettlements = response.data.data || []
          } else {
            this.availableSettlements = []
          }
        } else {
          console.error('获取结算单列表失败:', response.data?.message)
          this.availableSettlements = []
        }
      } catch (error) {
        console.error('获取结算单列表请求错误:', error)
        this.availableSettlements = []
      }
    },
    
    // 加载付款列表
    async loadPaymentList() {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.$message.warning('查询时间起不能晚于查询时间止')
        return
      }
      this.loading = true
      
      try {
        // 构建查询参数
        const params = {
          page: this.pagination.current - 1, // 转换为0基页码
          size: this.pagination.pageSize,
          sortBy: 'paymentDate',
          sortDir: 'desc'
        }
        
        // 添加搜索条件
        if (this.searchParams.paymentMethod) {
          params.paymentMethod = this.searchParams.paymentMethod
        }
        
        if (this.startDate) params.startDate = this.startDate
        if (this.endDate) params.endDate = this.endDate
        
        const response = await query_payments_pagination(params)
        
        // 处理分页响应格式
        if (response.data && response.data.success) {
          const data = response.data.data || {}
          if (data.content) {
            this.paymentList = data.content || []
            this.pagination.total = data.totalElements || 0
            this.pagination.current = (data.pageNumber || 0) + 1 // 转换为1基页码
          } else if (Array.isArray(response.data.data)) {
            this.paymentList = response.data.data
            this.pagination.total = response.data.data.length
          } else {
            this.paymentList = []
            this.pagination.total = 0
          }
        } else {
          console.error('获取付款列表失败:', response.data?.message)
          this.$message.error(response.data?.message || '获取付款列表失败')
          this.paymentList = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('获取付款列表请求错误:', error)
        this.$message.error('获取付款列表失败，请稍后重试')
        this.paymentList = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadPaymentList()
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.current = 1
      this.loadPaymentList()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.current = val
      this.loadPaymentList()
    },
    
    // 显示新增付款对话框
    showAddPaymentDialog() {
      this.paymentDialogTitle = '新增付款'
      this.paymentForm.id = null
      this.resetPaymentForm()
      this.paymentDialogVisible = true
    },
    
    // 编辑付款
    async editPayment(row) {
      this.paymentDialogTitle = '编辑付款'
      
      try {
        // 加载付款详情
        const response = await query_payment_by_id(row.id)
        
        if (response.data && response.data.success) {
          const paymentData = response.data.data
          
          this.paymentForm = {
            id: paymentData.id,
            paymentDate: paymentData.paymentDate,
            amount: paymentData.amount,
            paymentMethod: paymentData.paymentMethod,
            voucherUrl: paymentData.voucherUrl || '',
            remark: paymentData.remark || '',
            settlementIds: paymentData.settlementIds || (paymentData.settlements ? paymentData.settlements.map(s => s.id) : [])
          }
          
          // 如果有凭证路径，设置预览
          if (paymentData.voucherUrl) {
            this.voucherPreview = this.getAttachmentUrl(paymentData.voucherUrl)
          }
        } else {
          this.$message.error('加载付款详情失败')
          return
        }
      } catch (error) {
        console.error('加载付款详情失败:', error)
        this.$message.error('加载付款详情失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        return
      }
      
      this.paymentDialogVisible = true
    },
    
    // 删除付款
    deletePayment(row) {
      this.$confirm('确定要删除此付款记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await delete_payment(row.id)
          
          if (response.data && response.data.success) {
            this.$message.success('删除成功')
            this.loadPaymentList() // 重新加载列表
          } else {
            this.$message.error(response.data?.message || '删除失败')
          }
        } catch (error) {
          console.error('删除付款记录失败:', error)
          this.$message.error('删除失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {
        // 用户取消操作
      })
    },
    
    // 重置付款表单
    resetPaymentForm() {
      this.paymentForm = {
        id: null,
        paymentDate: '',
        amount: null,
        paymentMethod: '',
        voucherUrl: '',
        remark: '',
        settlementIds: []
      }
      this.voucherPreview = ''
      if (this.$refs.paymentForm) {
        this.$refs.paymentForm.resetFields()
      }
      if (this.$refs.voucherUpload) {
        this.$refs.voucherUpload.clearFiles()
      }
    },
    
    // 提交付款表单
    async submitPaymentForm() {
      // 先进行表单验证
      try {
        await this.$refs.paymentForm.validate()
      } catch (error) {
        // 验证失败，不执行提交
return false
      }
      
      try {
        this.submitLoading = true
        
        // 构建提交数据
        const submitData = {
          paymentDate: this.paymentForm.paymentDate,
          amount: this.paymentForm.amount,
          paymentMethod: this.paymentForm.paymentMethod,
          settlementIds: this.paymentForm.settlementIds
        }
        
        // 如果有文件路径，添加到提交数据
        if (this.paymentForm.voucherUrl) {
          submitData.voucherUrl = this.paymentForm.voucherUrl
        }
        
        // 如果有备注，添加到提交数据
        if (this.paymentForm.remark) {
          submitData.remark = this.paymentForm.remark
        }
        
        // 如果是编辑模式，添加ID
        if (this.paymentForm.id) {
          submitData.id = this.paymentForm.id
        }
        
        // 调用API保存付款
        const response = await save_payment(submitData)
        
        if (response.data && response.data.success) {
          this.$message.success(this.paymentForm.id ? '编辑成功' : '新增成功')
          this.paymentDialogVisible = false
          this.loadPaymentList() // 重新加载列表
        } else {
          this.$message.error(response.data?.message || '保存失败')
        }
      } catch (error) {
        console.error('保存付款记录失败:', error)
        this.$message.error('保存失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      } finally {
        this.submitLoading = false
      }
    },
    
    // 文件上传前验证
    beforeFileUpload(file) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过10MB')
        return false
      }
      return true
    },
    
    // 上传付款凭证
    async handleVoucherUpload(options) {
      const file = options.file
      
      try {
        // 确定relatedId：编辑模式使用付款ID，新增模式使用结算单ID（如果已选择）
        let relatedId = null
        if (this.paymentForm.id) {
          // 编辑模式：使用付款ID
          relatedId = this.paymentForm.id
        } else {
          // 新增模式：如果有结算单ID则使用，否则使用0（临时上传）
          relatedId = (this.paymentForm.settlementIds && this.paymentForm.settlementIds.length > 0) 
            ? this.paymentForm.settlementIds[0] 
            : 0
        }
        
        // 如果是图片，生成预览
        if (file.type.startsWith('image/')) {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.voucherPreview = e.target.result
          }
          reader.readAsDataURL(file)
        }
        
        // 使用通用附件上传API
        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', 'payment')
        formData.append('relatedId', relatedId)
        
        const response = await upload_attachment(formData)
        
        if (response.data && response.data.success) {
          this.paymentForm.voucherUrl = response.data.data?.filePath || ''
          this.$message.success('付款凭证上传成功')
          options.onSuccess(response.data)
        } else {
          this.$message.error(response.data?.message || '上传失败')
          options.onError(new Error(response.data?.message || '上传失败'))
        }
      } catch (error) {
        console.error('上传付款凭证失败:', error)
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        options.onError(error)
      }
    },
    
    // 清除凭证
    clearVoucher() {
      this.paymentForm.voucherUrl = ''
      this.voucherPreview = ''
      if (this.$refs.voucherUpload) {
        this.$refs.voucherUpload.clearFiles()
      }
    },
    
    // 格式化金额
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('YYYY-MM-DD')
    },
    
    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('YYYY-MM-DD HH:mm:ss')
    },
    
    // 获取附件下载URL（用于显示图片）
    getAttachmentUrl(filePath) {
      if (!filePath) return ''
      
      // 如果是完整的URL（http/https开头），直接返回
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath
      }
      
      // 如果是data URL（base64），直接返回
      if (filePath.startsWith('data:')) {
        return filePath
      }
      
      // 否则，使用附件下载接口
      const apiUrl = getApiUrl('/attachments/download')
      return `${apiUrl}?filePath=${encodeURIComponent(filePath)}`
    },
    
    // 获取付款方式名称
    getPaymentMethodName(method) {
      const methodMap = {
        'CASH': '现金',
        'BANK_TRANSFER': '转账',
        'MOBILE_PAYMENT': '移动支付',
        'REMITTANCE': '汇款'
      }
      return methodMap[method] || method
    },
    
    // 获取付款方式标签类型
    getPaymentMethodTagType(method) {
      const typeMap = {
        'CASH': 'success',
        'BANK_TRANSFER': 'primary',
        'MOBILE_PAYMENT': 'warning',
        'REMITTANCE': 'info'
      }
      return typeMap[method] || ''
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-management-container {
  padding: 20px;
  background-color: #fff;
  
  .page-header {
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .search-area {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    
    .search-input {
      width: 200px;
    }
    
    .date-picker {
      width: 200px;
    }
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
  
  // 付款凭证上传样式
  .payment-uploader {
    ::v-deep .el-upload {
      border: 1px dashed #d9d9d9;
      border-radius: 6px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      width: 100px;
      height: 100px;
      
      &:hover {
        border-color: #409EFF;
      }
    }
    
    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      
      .payment-uploader-icon {
        font-size: 28px;
        color: #8c939d;
        margin-bottom: 8px;
      }
    }
    
    .payment-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 100px;
      height: 100px;
      line-height: 100px;
      text-align: center;
    }
    
    .payment-image {
      width: 100px;
      height: 100px;
      display: block;
      object-fit: cover;
    }
  }
  
  .file-path {
    margin-top: 8px;
    
    .el-tag {
      max-width: 100%;
      word-break: break-all;
    }
  }
  
  .el-upload__tip {
    margin-top: 5px;
    font-size: 12px;
    color: #606266;
  }
}
</style>
